/*
 * The study's name, drawn into the 640 × 360 art at its own pixel size instead of laid over it:
 *  - chalk: written across the chalkboard, the welcome scrawled along its foot
 *  - wood:  a carved board hanging on chains from the candle wheel, the crest burned beside the name
 * Letters come from Silkscreen rendered at a whole multiple of its pixel grid, then snapped to on/off.
 */


export type SignStyle = 'chalk' | 'wood';

type Mask = { w: number; h: number; on: (x: number, y: number) => boolean };

/** Text as a hard-edged pixel mask (scale 1 → 8px Silkscreen, one art pixel per font pixel) */
export function textMask(text: string, scale: number, bold = false): Mask {
  const size = 8 * scale;
  const c = document.createElement('canvas');
  const x = c.getContext('2d')!;
  x.font = `${bold ? 700 : 400} ${size}px Silkscreen`;
  const w = Math.ceil(x.measureText(text).width) + 2, h = size + 2;
  c.width = w; c.height = h;
  x.font = `${bold ? 700 : 400} ${size}px Silkscreen`;
  x.textBaseline = 'top';
  x.fillStyle = '#fff';
  x.fillText(text, 1, 1);
  const d = x.getImageData(0, 0, w, h).data;
  // Trim to the inked rows so placement is exact
  let top = h, bottom = 0;
  for (let yy = 0; yy < h; yy++) for (let xx = 0; xx < w; xx++) if (d[(yy * w + xx) * 4 + 3] > 127) { top = Math.min(top, yy); bottom = Math.max(bottom, yy); }
  if (top > bottom) return { w: 0, h: 0, on: () => false };
  return { w, h: bottom - top + 1, on: (xx, yy) => xx >= 0 && yy >= 0 && xx < w && yy + top < h && d[((yy + top) * w + xx) * 4 + 3] > 127 };
}

/** Text in any loaded font (CSS font shorthand), snapped to on/off pixels at the art's size */
export function fontMask(text: string, font: string, cut = 110, spacing = 0): Mask {
  const c = document.createElement('canvas');
  const x = c.getContext('2d')!;
  x.font = font; (x as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = `${spacing}px`;
  const size = Number(font.match(/(\d+)px/)?.[1] ?? 12);
  const w = Math.ceil(x.measureText(text).width) + 4, h = Math.ceil(size * 1.7) + 2;
  c.width = w; c.height = h;
  x.font = font; (x as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = `${spacing}px`; x.textBaseline = 'top'; x.fillStyle = '#fff';
  x.fillText(text, 2, 1);
  const d = x.getImageData(0, 0, w, h).data;
  let top = h, bottom = 0;
  for (let yy = 0; yy < h; yy++) for (let xx = 0; xx < w; xx++) if (d[(yy * w + xx) * 4 + 3] > cut) { top = Math.min(top, yy); bottom = Math.max(bottom, yy); }
  if (top > bottom) return { w: 0, h: 0, on: () => false };
  return { w, h: bottom - top + 1, on: (xx, yy) => xx >= 0 && yy >= 0 && xx < w && yy + top < h && d[((yy + top) * w + xx) * 4 + 3] > cut };
}
export const CHALK_FONT = '"Grenze Gotisch"';

/* A compact 5-pixel-tall capital alphabet for small lettering (book spines), hand set like pixel-art type */
const TINY: Record<string, string[]> = {
  A: ['.#.', '#.#', '###', '#.#', '#.#'], B: ['##.', '#.#', '##.', '#.#', '##.'], C: ['.##', '#..', '#..', '#..', '.##'],
  D: ['##.', '#.#', '#.#', '#.#', '##.'], E: ['###', '#..', '##.', '#..', '###'], F: ['###', '#..', '##.', '#..', '#..'],
  G: ['.##', '#..', '#.#', '#.#', '.##'], H: ['#.#', '#.#', '###', '#.#', '#.#'], I: ['###', '.#.', '.#.', '.#.', '###'],
  J: ['..#', '..#', '..#', '#.#', '.#.'], K: ['#.#', '#.#', '##.', '#.#', '#.#'], L: ['#..', '#..', '#..', '#..', '###'],
  M: ['#...#', '##.##', '#.#.#', '#...#', '#...#'], N: ['#..#', '##.#', '#.##', '#..#', '#..#'], O: ['.#.', '#.#', '#.#', '#.#', '.#.'],
  P: ['##.', '#.#', '##.', '#..', '#..'], Q: ['.#.', '#.#', '#.#', '##.', '.##'], R: ['##.', '#.#', '##.', '#.#', '#.#'],
  S: ['.##', '#..', '.#.', '..#', '##.'], T: ['###', '.#.', '.#.', '.#.', '.#.'], U: ['#.#', '#.#', '#.#', '#.#', '###'],
  V: ['#.#', '#.#', '#.#', '#.#', '.#.'], W: ['#...#', '#...#', '#.#.#', '##.##', '#...#'], X: ['#.#', '#.#', '.#.', '#.#', '#.#'],
  Y: ['#.#', '#.#', '.#.', '.#.', '.#.'], Z: ['###', '..#', '.#.', '#..', '###'], ' ': ['..', '..', '..', '..', '..'],
};
/** Text in the compact alphabet, as a pixel mask */
export function tinyMask(text: string): Mask {
  const glyphs = [...text.toUpperCase()].map(ch => TINY[ch] ?? TINY[' ']);
  const w = glyphs.reduce((n, g) => n + g[0].length + 1, -1);
  const cols: string[] = Array(5).fill('');
  glyphs.forEach((g, i) => g.forEach((row, y) => { cols[y] += row + (i < glyphs.length - 1 ? '.' : ''); }));
  return { w, h: 5, on: (x, y) => cols[y]?.[x] === '#' };
}

const rgb = (c: number[]) => `rgb(${c[0]},${c[1]},${c[2]})`;
const hash = (x: number, y: number) => { const h = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453; return h - Math.floor(h); };

/* ── Chalk ── */
const BOARD = [88, 61, 28], BOARD_2 = [84, 58, 26], CHALK = [214, 206, 156], CHALK_DIM = [168, 160, 112];
const BOARD_IN = { x0: 226, x1: 406, y0: 81, y1: 184 };

/* The candle standing in front of the board's lower right corner: its own flame and wax are kept from
   the art, and a fresh glow is laid on the clean slate around it */
const CANDLE_X = 381;
const FLAME = { x0: 381, x1: 390, y0: 161, y1: 175, cx: 385, cy: 169 };
const WAX = { x0: 381, x1: 389, y0: 176, y1: 183 };

function chalk(x: CanvasRenderingContext2D, name: string, art: Uint8ClampedArray | null) {
  const at = (X: number, Y: number) => { const i = (Y * 640 + X) * 4; return art ? [art[i], art[i + 1], art[i + 2]] : [0, 0, 0]; };
  const inFront = (X: number, Y: number) => {
    if (!art) return false;
    const [r, g] = at(X, Y);
    if (X >= FLAME.x0 && X <= FLAME.x1 && Y >= FLAME.y0 && Y <= FLAME.y1) return r > 180 && g > 105;
    if (X >= WAX.x0 && X <= WAX.x1 && Y >= WAX.y0 && Y <= WAX.y1) return r > 150 && g > 120;
    return false;
  };
  // Candlelight on the slate: rings of warmer board, dithered so the steps don't band
  const GLOW = [[132, 92, 42], [116, 80, 36], [102, 71, 32]];
  const lit = (X: number, Y: number, c: number[]) => {
    const d = Math.hypot((X - FLAME.cx) * 0.9, Y - FLAME.cy);
    if (d > 15) return c;
    const ring = d < 5 ? 0 : d < 9 ? 1 : 2, edge = (d < 5 ? d / 5 : d < 9 ? (d - 5) / 4 : (d - 9) / 6);
    const bayer = [[0, 8, 2, 10], [12, 4, 14, 6], [3, 11, 1, 9], [15, 7, 13, 5]][Y & 3][X & 3] / 16;
    return edge > bayer ? (ring === 2 ? c : GLOW[ring + 1]) : GLOW[ring];
  };
  const px = (X: number, Y: number, c: number[]) => {
    if (inFront(X, Y)) { x.fillStyle = rgb(at(X, Y)); x.fillRect(X, Y, 1, 1); return; }
    x.fillStyle = rgb(c === BOARD || c === BOARD_2 ? lit(X, Y, c) : c); x.fillRect(X, Y, 1, 1);
  };
  const B = BOARD_IN;
  // Wipe the whole board: plain slate with the faint ghost of old chalk rubbed into it
  for (let Y = B.y0; Y <= B.y1; Y++) for (let X = B.x0; X <= B.x1; X++) {
    const r = hash(X * 3, Y);
    px(X, Y, r < 0.012 ? [104, 86, 50] : r < 0.8 ? BOARD : BOARD_2);
  }
  // Chalk strokes: bright with the odd skip where the grain catches
  const dot = (X: number, Y: number, dim = false) => {
    X = Math.round(X); Y = Math.round(Y);
    if (X < B.x0 + 1 || X > B.x1 - 1 || Y < B.y0 + 1 || Y > B.y1 - 1) return;
    const r = hash(X, Y);
    if (r < 0.06) return;
    px(X, Y, dim || r < 0.22 ? CHALK_DIM : CHALK);
  };
  const write = (m: Mask, X0: number, Y0: number, dim = false) => {
    for (let Y = 0; Y < m.h; Y++) for (let X = 0; X < m.w; X++) if (m.on(X, Y)) dot(X0 + X, Y0 + Y, dim);
  };
  const line = (x0: number, y0: number, x1: number, y1: number, dim = true) => {
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
    for (let i = 0; i <= n; i++) dot(x0 + ((x1 - x0) * i) / n, y0 + ((y1 - y0) * i) / n, dim);
  };

  // Title, underlined
  const title = fontMask('The Study', `500 26px ${CHALK_FONT}`, 140, 1);
  const tx = Math.round((B.x0 + B.x1 - title.w) / 2);
  write(title, tx, B.y0 + 4);
  for (let X = tx - 2; X < tx + title.w + 2; X++) if (hash(X, 7) > 0.15) px(X, B.y0 + title.h + 7, CHALK_DIM);

  // The golden spiral: Fibonacci squares cut from an 89 × 55 rectangle, a quarter-circle in each
  const W = 76, H = 47, gx = Math.round((B.x0 + B.x1 - W) / 2) + 6, gy = B.y0 + title.h + 10;
  let r = { x: gx, y: gy, w: W, h: H };
  line(gx, gy, gx + W, gy); line(gx, gy + H, gx + W, gy + H); line(gx, gy, gx, gy + H); line(gx + W, gy, gx + W, gy + H);
  const labels: [number, number, number][] = [];
  for (let k = 0; k < 9; k++) {
    const s = Math.min(r.w, r.h); if (s < 1) break;
    let sq: { x: number; y: number }, cx: number, cy: number, t0: number;
    switch (k % 4) {
      case 0: sq = { x: r.x, y: r.y }; cx = r.x + s; cy = r.y + s; t0 = Math.PI; r = { x: r.x + s, y: r.y, w: r.w - s, h: r.h }; break;
      case 1: sq = { x: r.x, y: r.y }; cx = r.x; cy = r.y + s; t0 = 1.5 * Math.PI; r = { x: r.x, y: r.y + s, w: r.w, h: r.h - s }; break;
      case 2: sq = { x: r.x + r.w - s, y: r.y }; cx = r.x + r.w - s; cy = r.y; t0 = 0; r = { x: r.x, y: r.y, w: r.w - s, h: r.h }; break;
      default: sq = { x: r.x, y: r.y + r.h - s }; cx = r.x + s; cy = r.y + r.h - s; t0 = 0.5 * Math.PI; r = { x: r.x, y: r.y, w: r.w, h: r.h - s }; break;
    }
    // The square's inner edge (the outer ones are the rectangle)
    if (k % 4 === 0) line(sq.x + s, sq.y, sq.x + s, sq.y + s);
    if (k % 4 === 1) line(sq.x, sq.y + s, sq.x + s, sq.y + s);
    if (k % 4 === 2) line(sq.x, sq.y, sq.x, sq.y + s);
    if (k % 4 === 3) line(sq.x, sq.y, sq.x + s, sq.y);
    const steps = Math.max(8, Math.ceil(s * 2));
    for (let i = 0; i <= steps; i++) { const t = t0 + (Math.PI / 2) * (i / steps); dot(cx + s * Math.cos(t), cy + s * Math.sin(t)); }
    if (s >= 11) labels.push([sq.x + 2, sq.y + s - 7, [55, 34, 21, 13, 8][k]]); // the Fibonacci number each square stands for
  }
  for (const [lx, ly, n] of labels) write(textMask(String(n), 1), lx, ly, true);

  // Fib levels down the left, each with its tick
  const levels = ['1.618', '1.000', '0.786', '0.618', '0.500', '0.382'];
  const lx = B.x0 + 6, step = Math.floor((H + 4) / (levels.length - 1));
  levels.forEach((lv, i) => {
    const m = textMask(lv, 1), y = gy - 2 + i * step;
    write(m, lx, y, lv !== '0.618' && lv !== '1.618');
    line(lx + m.w + 2, y + 2, lx + m.w + 8, y + 2);
  });
  // φ on the right
  const phi = fontMask('Phi', `500 17px ${CHALK_FONT}`, 140, 1), val = textMask('1.618', 1);
  write(phi, B.x1 - 6 - phi.w, gy + 14);
  write(val, B.x1 - 6 - val.w, gy + 14 + phi.h + 3, true);

  // The welcome along the foot
  const welcome = fontMask(`Welcome, ${name}`, `400 15px ${CHALK_FONT}`, 150, 0.5);
  // Centred, but kept clear of the candle standing at the right
  write(welcome, Math.min(Math.round((B.x0 + B.x1 - welcome.w) / 2), CANDLE_X - 5 - welcome.w), B.y1 - welcome.h - 4);
}

/* ── Wood ── */
const OUT = [52, 28, 14], WOOD_S = [112, 68, 32], WOOD = [146, 98, 44], WOOD_H = [178, 128, 54], BURN = [58, 30, 14], BURN_LIT = [196, 148, 66];
const CHAIN = [70, 62, 52], CHAIN_H = [140, 128, 104];
const SIGN = { cx: 320, y: 66, h: 24, from: 56 };

function wood(x: CanvasRenderingContext2D, name: string, sway: number) {
  const px = (X: number, Y: number, c: number[]) => { x.fillStyle = rgb(c); x.fillRect(X + sway, Y, 1, 1); };
  // The plank is cut to fit the name with the crest beside it
  const t = textMask('THE STUDY', 2, true);
  const { y, h } = SIGN, w = t.w + 34, X0 = SIGN.cx - Math.round(w / 2);
  const hang = [X0 + 14, X0 + w - 15];
  // Chains from the wheel's ring down to the board's top corners
  for (const cx of hang) for (let Y = SIGN.from; Y < y; Y++) {
    const link = (Y - SIGN.from) % 3;
    px(cx, Y, link === 1 ? CHAIN_H : CHAIN); if (link === 0) { px(cx - 1, Y, CHAIN); px(cx + 1, Y, CHAIN); }
  }
  // Plank: dark outline, planed face with grain, lit top edge, shaded bottom, nail heads
  for (let Y = 0; Y < h; Y++) for (let X = 0; X < w; X++) {
    const edge = X === 0 || Y === 0 || X === w - 1 || Y === h - 1;
    const corner = (X < 2 || X > w - 3) && (Y < 2 || Y > h - 3);
    if (corner && (X === 0 || X === w - 1) && (Y === 0 || Y === h - 1)) continue;
    let c = WOOD;
    if (edge) c = OUT;
    else if (Y === 1) c = WOOD_H;
    else if (Y >= h - 3) c = WOOD_S;
    else { const g = Math.sin((X + Y * 7) * 0.21 + Math.sin(Y * 0.9) * 2) + hash(X, Y) * 0.8; c = g > 1.1 ? WOOD_H : g < -0.9 ? WOOD_S : WOOD; }
    px(X0 + X, y + Y, c);
  }
  for (const cx of hang) { px(cx, y + 2, OUT); px(cx, y + 3, CHAIN_H); }
  // Crest, burned in at the left: a small shield with the sword in the stone
  const sx = X0 + 6, sy = y + 4;
  const shield = ['..#######..', '.#.......#.', '.#...#...#.', '.#..###..#.', '.#...#...#.', '.#...#...#.', '.#..###..#.', '..#.###.#..', '...#...#...', '....#.#....', '.....#.....'];
  shield.forEach((row, j) => [...row].forEach((ch, i) => { if (ch === '#') { px(sx + i, sy + j, BURN); if (j < shield.length - 1 && shield[j + 1][i] !== '#') px(sx + i, sy + j + 1, BURN_LIT); } }));
  // Name, burned into the face with a lit lower lip on each stroke
  const tx = X0 + 22, ty = y + Math.round((h - t.h) / 2);
  for (let Y = 0; Y < t.h; Y++) for (let X = 0; X < t.w; X++) if (t.on(X, Y)) {
    px(tx + X, ty + Y, BURN);
    if (!t.on(X, Y + 1)) px(tx + X, ty + Y + 1, BURN_LIT);
  }
  // A small tag below on a short cord: the welcome
  const wl = textMask(`WELCOME, ${name.toUpperCase()}`, 1);
  const tw = wl.w + 8, th = wl.h + 6, tagX = X0 + Math.round((w - tw) / 2), tagY = y + h + 4;
  for (let Y = y + h; Y < tagY; Y++) { px(tagX + 6, Y, CHAIN); px(tagX + tw - 7, Y, CHAIN); }
  for (let Y = 0; Y < th; Y++) for (let X = 0; X < tw; X++) {
    const edge = X === 0 || Y === 0 || X === tw - 1 || Y === th - 1;
    px(tagX + X, tagY + Y, edge ? OUT : Y === 1 ? WOOD_H : Y >= th - 2 ? WOOD_S : WOOD);
  }
  for (let Y = 0; Y < wl.h; Y++) for (let X = 0; X < wl.w; X++) if (wl.on(X, Y)) px(tagX + 4 + X, tagY + 3 + Y, BURN);
}

/* ── The crest pressed into the cloth under the board ── */
/* The header crest, captured at full size and brought down to the cloth's pixel grid:
   x = its ink and cut-outs (rim, seam, star, sword, stone), o = its silver face, . = outside */
const CREST = [
  '..........xxxxxxxxxx..........',
  '.......xxxooooooooooxxxx......',
  '...xxxooooooxxxxxxooooooxxx...',
  '.xxoooooxxxxooooooxxxxoooooxx.',
  'xooooxxxooooooooooooooxxxoooox',
  'xoxxxooooooooooooooooooooxxxox',
  'xoxooooxxooooooooooooooooooxox',
  'xoxooooxxooooooooooooooooooxox',
  'xoxooxxxxxxooooooooooooooooxox',
  'xoxooxxxxxxooooooooooooooooxox',
  'xoxooooxxooooooooooooooxxooxox',
  'xoxooooxxooooooooooooooxxooxox',
  'xoxoooooooooooooooooooxxxooxox',
  'xoxooooooooooooooxxxxxxxoooxox',
  'xoxoooooooooooooxxxxxxxooooxox',
  'xoxooooooooooooooooxxxxxoooxox',
  'xoxooooooooooooooooxxxxxxooxox',
  'xoxoooooooooooooooxxxxxxxxoxox',
  'xoxooooooooooooooxxoxxooxxoxox',
  'xoxooooooooooooooxxoxxoooooxox',
  'xoxoooooooooooooxxoxxooooooxox',
  'xoxoooooooooooooxxoxxooooooxox',
  'xoxooooooooooooxxoxxoooooooxox',
  'xxxooooooooooooxxxxxoooooooxox',
  '.xoxoooxxxxxxoxxoxxoooooooxoxx',
  '.xoxoooxxxxxxxooxxooooooooxox.',
  '.xxoxooxxxxxxxxoxxoooooooxxxx.',
  '..xoxoxxxxxxxxxoxooooooooxox..',
  '..xxoxxxxxxxxxxxooooooooxoxx..',
  '...xoxxxxxxxxxxxxoooooooxox...',
  '...xxoxxxxxxxxxxxxoooooxoxx...',
  '....xxoxxxxxxxxxxxxxooxoxx....',
  '.....xxxxxxxxxxxxxxxxxxox.....',
  '.....xxoxxxxxxxxxxxxxxoxx.....',
  '......xxoxxxxxxxxxxxxoxx......',
  '.......xxoxxxxxxxxxxoxx.......',
  '........xxoxxxxxxxxoxx........',
  '.........xxoxxxxxxoxxx........',
  '.........xxxoxxxxoxxx.........',
  '..........xxxoxxoxxx..........',
  '...........xxxooxxx...........',
  '............xxxxxx............',
  '.............xxxx.............',
  '..............xx..............',
];
const CLOTH = { cx: 308, top: 202 };

function crestOnCloth(x: CanvasRenderingContext2D, art: Uint8ClampedArray) {
  const W = CREST[0].length, X0 = CLOTH.cx - Math.floor(W / 2), Y0 = CLOTH.top;
  const at = (X: number, Y: number) => CREST[Y]?.[X] ?? '.';
  const put = (X: number, Y: number, k: number) => {
    const i = ((Y0 + Y) * 640 + X0 + X) * 4;
    x.fillStyle = rgb([art[i] * k, art[i + 1] * k, art[i + 2] * k].map(v => Math.min(255, Math.round(v))));
    x.fillRect(X0 + X, Y0 + Y, 1, 1);
  };
  // Pressed into the leather: the crest's ink and cut-outs deep, its face shallow; each pressed edge
  // has a lit lower lip
  for (let Y = 0; Y <= CREST.length; Y++) for (let X = 0; X < W; X++) {
    const c = at(X, Y), up = at(X, Y - 1);
    if (c === 'x') put(X, Y, 0.45);
    else if (c === 'o') put(X, Y, up === 'x' ? 1.02 : 0.9);
    else if (up !== '.') put(X, Y, 1.12);
  }
}

/** Draw the sign; `t` (seconds) gives the hanging board its slow sway */
export function drawSign(x: CanvasRenderingContext2D, style: SignStyle, name: string, t: number, art: Uint8ClampedArray | null = null) {
  x.clearRect(0, 0, x.canvas.width, x.canvas.height);
  if (art) crestOnCloth(x, art);
  if (style === 'chalk') chalk(x, name, art);
  else wood(x, name, Math.sin(t * 1.1) > 0.75 ? 1 : Math.sin(t * 1.1) < -0.75 ? -1 : 0);
}
